import { useEffect } from "react";

import { useLoadDeploymentsCurrent } from "Reducers/environment/deployment";
import { selectProjectTasksEnabled } from "Reducers/project/capabilities";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import {
  selectEnvironmentTasksError,
  selectEnvironmentTasksHasLoaded,
  selectEnvironmentTasksIsLoading,
  selectTaskContainerRows,
  selectTaskContainersRunningByTask,
  selectTaskContainersRunningRowCount,
  selectTaskContainersRunningCount,
  selectTaskStatusByName
} from "../environmentTasks.selectors";
import { ensureEnvironmentTasksLoaded } from "../thunks/ensureEnvironmentTasksLoaded.thunk";
import { fetchEnvironmentTasksThunk } from "../thunks/fetchEnvironmentTasks.thunk";
import { runEnvironmentTaskThunk } from "../thunks/runEnvironmentTask.thunk";

import type {
  EnvironmentTasksParams,
  TaskContainerRow,
  TaskStatus
} from "../types";

const EMPTY_ROWS: TaskContainerRow[] = [];
const EMPTY_RUNNING: Record<string, boolean> = {};
const EMPTY_STATUS_BY_NAME: Record<string, TaskStatus> = {};

/**
 * Single data dependency for the Task containers card. Loads definitions +
 * activities (once, idempotently), keeps the deployment topology loaded for the
 * allocation size, and exposes the joined rows, status counts, and the run
 * action. Components stay dumb — all joining lives in the selectors.
 */
export const useEnvironmentTasks = (
  params: Partial<EnvironmentTasksParams>
) => {
  const dispatch = useAppDispatch();
  const { organizationId, projectId, environmentId } = params;
  const ready = Boolean(organizationId && projectId && environmentId);
  const fullParams = {
    organizationId,
    projectId,
    environmentId
  } as EnvironmentTasksParams;

  // Gate on the project `tasks` capability. `undefined` means the capability
  // hasn't loaded yet (loaded by the parent project page) — treat as unknown and
  // hold off until it resolves. `false` means the project can't run tasks, so we
  // skip the fetch entirely (the tasks endpoint otherwise returns `200 []` and
  // the card would wrongly fall back to the empty promo).
  const tasksEnabled = useAppSelector(state =>
    ready && organizationId && projectId
      ? selectProjectTasksEnabled(state, { organizationId, projectId })
      : undefined
  );
  const shouldLoad = ready && tasksEnabled === true;

  // Allocation size comes from the deployment topology — keep it loaded.
  useLoadDeploymentsCurrent(fullParams, shouldLoad);

  const rows = useAppSelector(state =>
    ready ? selectTaskContainerRows(state, fullParams) : EMPTY_ROWS
  );

  const runningCount = useAppSelector(state =>
    ready ? selectTaskContainersRunningCount(state, fullParams) : 0
  );
  const runningRowCount = useAppSelector(state =>
    ready ? selectTaskContainersRunningRowCount(state, fullParams) : 0
  );
  const isLoading = useAppSelector(state =>
    ready ? selectEnvironmentTasksIsLoading(state, fullParams) : false
  );
  const hasLoaded = useAppSelector(state =>
    ready ? selectEnvironmentTasksHasLoaded(state, fullParams) : false
  );
  const error = useAppSelector(state =>
    ready ? selectEnvironmentTasksError(state, fullParams) : null
  );
  const runningByTask = useAppSelector(state =>
    ready ? selectTaskContainersRunningByTask(state, fullParams) : EMPTY_RUNNING
  );
  const statusByName = useAppSelector(state =>
    ready ? selectTaskStatusByName(state, fullParams) : EMPTY_STATUS_BY_NAME
  );
  const reload = () => {
    if (ready) return dispatch(fetchEnvironmentTasksThunk(fullParams));
  };

  const runTask = (taskName: string, variables?: Record<string, unknown>) => {
    if (ready) {
      return dispatch(
        runEnvironmentTaskThunk({ ...fullParams, taskName, variables })
      ).unwrap();
    }
    return Promise.reject(new Error("Not ready"));
  };

  useEffect(() => {
    if (shouldLoad) dispatch(ensureEnvironmentTasksLoaded(fullParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, shouldLoad, organizationId, projectId, environmentId]);

  return {
    rows,
    runningCount,
    runningRowCount,
    isLoading,
    hasLoaded,
    error,
    reload,
    runTask,
    runningByTask,
    statusByName,
    tasksEnabled
  };
};
